import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../api';

export interface AssessmentSchedule {
  msAssessmentScheduleID: string;
  name: string;
  type: string;
  programId: string;
  programName: string;
  learningObjectives: string;
  description: string;
  isWithQuestion: string;
  startTime: string;
  endTime: string;
  notificationStatus: string;
  notes?: string;
  statusKurikulum: boolean;
  isBeforeSchedule: boolean;
  previousScheduleId?: string;
  notificationSchedule: string;
  isDeleted: boolean;
  dateIn: string;
  dateUp: string;
  userIn: string;
  userUp: string;
}

export interface Module {
  moduleId: string;
  moduleName?: string;
}

export interface Participant {
  binusianId: string;
  link?: string;
  name?: string;
  division?: string;
  type?: string;
}

export interface Respondent {
  binusianId: string;
  participantId?: string;
  respondentType: string;
  division: string;
  name?: string;
}

interface CreateAssessmentScheduleDto {
  name: string;
  type: string;
  programId: string;
  learningObjectives: string;
  description: string;
  isWithQuestion: string;
  startTime: string;
  endTime: string;
  notificationStatus: string;
  notes: string;
  statusKurikulum: boolean;
  isBeforeSchedule: boolean;
  previousScheduleId?: string;
  notificationSchedule: string;
  modules: { moduleId: string }[];
  participants: {
    binusianId: string;
    listModule: string[];
    link?: string;
  }[];
  respondents: {
    binusianId: string;
    participantId?: string;
    respondentType: string;
    division: string;
    listModule?: string[];
  }[];
}

export interface UpdateAssessmentScheduleDto extends CreateAssessmentScheduleDto {
  msAssessmentScheduleId: string;
}

export const assessmentScheduleService = {
  getList: async (params?: { search?: string; page?: number; limit?: number }) => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.ASSESSMENT_SCHEDULE.LIST, {
        params: {
          pageNumber: params?.page || 1,
          pageSize: params?.limit || 10,
          search: params?.search || ''
        }
      });
      return response.data;
    } catch {
      return {
        pageNumber: params?.page || 1,
        pageSize: params?.limit || 10,
        totalPages: 0,
        totalRecords: 0,
        message: 'Failed to fetch data',
        data: []
      };
    }
  },

  getDetail: async (id: string) => {
    const response = await httpClient.get(API_ENDPOINTS.ASSESSMENT_SCHEDULE.DETAIL(id));
    return response.data;
  },

  create: async (data: CreateAssessmentScheduleDto) => {
    const response = await httpClient.post(API_ENDPOINTS.ASSESSMENT_SCHEDULE.CREATE, data);
    return response.data;
  },

  update: async (data: UpdateAssessmentScheduleDto) => {
    const response = await httpClient.put(API_ENDPOINTS.ASSESSMENT_SCHEDULE.UPDATE, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await httpClient.delete(API_ENDPOINTS.ASSESSMENT_SCHEDULE.DELETE, {
      data: { msAssessmentScheduleID: id }
    });
    return response.data;
  },

  copySchedule: async (scheduleId: string, data: any) => {
    const response = await httpClient.post(
      API_ENDPOINTS.ASSESSMENT_SCHEDULE.COPY(scheduleId),
      data
    );
    return response.data;
  },

  getPublicListAssessmentSchedule: async (
    encryptedEmail: string,
    params?: { search?: string; page?: number; limit?: number }
  ) => {
    try {
      const response = await httpClient.get(
        API_ENDPOINTS.ASSESSMENT_SCHEDULE.PUBLIC_LIST(encryptedEmail),
        {
          params: {
            pageNumber: params?.page || 1,
            pageSize: params?.limit || 10,
            search: params?.search || ''
          }
        }
      );
      return response.data;
    } catch {
      return {
        pageNumber: params?.page || 1,
        pageSize: params?.limit || 10,
        totalPages: 0,
        totalRecords: 0,
        message: 'Failed to fetch data',
        data: []
      };
    }
  },

  /* eslint-disable */
  getPublicDetailAssessmentScheduleByEmail: async (encryptedEmail: string) => {
    try {
      const response = await httpClient.get(
        API_ENDPOINTS.ASSESSMENT_SCHEDULE.PUBLIC_DETAIL_BY_EMAIL(encryptedEmail)
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  submitAssessmentResponseByEmail: async (encryptedEmail: string, data: any) => {
    try {
      const response = await httpClient.post(
        API_ENDPOINTS.ASSESSMENT_SCHEDULE.PUBLIC_SUBMIT_BY_EMAIL,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /* eslint-enable */

  uploadModuleEvidence: async (formData: FormData) => {
    const response = await httpClient.post(
      `${API_ENDPOINTS.ASSESSMENT_SCHEDULE.UPLOAD_MODULE_EVIDENCE}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  getExternalAssessmentDetail: async (encryptedId: string) => {
    try {
      const response = await httpClient.get(`/assessment-schedules/external/detail/${encryptedId}`);

      if (response && response.data) {
        return response.data;
      }
      return null;
    } catch {
      throw new Error('Failed to fetch data');
    }
  },

  /* eslint-disable */
  submitInternalEvidence: async (data: any) => {
    try {
      const response = await httpClient.post(
        `/assessment-schedules/internal/upload-evidence`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /* eslint-enable */

  submitExternalAssessmentResponse: async (
    encryptedId: string,
    email: string,
    responseData: any
  ) => {
    const response = await httpClient.post('/assessment-schedules/external/submit/', {
      hash: encryptedId,
      ...responseData,
      externalEmail: email
    });

    return response.data;
  },

  uploadExternalModuleEvidence: async (encryptedId: string, formData: FormData) => {
    const response = await httpClient.post(
      `/assessment-schedules/external/upload-evidence/${encryptedId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  },

  /* eslint-disable */
  exportAssessmentData: async (id: string, category?: string) => {
    try {
      const params: any = {
        MsAssessmentScheduleID: id
      };

      // Add category parameter if provided
      if (category && category !== 'all') {
        params.category = category;
      }

      const response = await httpClient.get('/assessment-schedules/export', {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSMEDetailAssessmentSchedule: async (encryptedEmail: string) => {
    try {
      const response = await httpClient.get(
        API_ENDPOINTS.ASSESSMENT_SCHEDULE.SME_DETAIL(encryptedEmail)
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  submitSMEEvidenceStatus: async (
    encryptedEmail: string,
    data: { evidences: { evidenceID: string; status: string }[] }
  ) => {
    try {
      const response = await httpClient.post(API_ENDPOINTS.ASSESSMENT_SCHEDULE.SME_SUBMIT, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  searchCourse: async (params: { search?: string; pageNumber?: number; pageSize?: number }) => {
    const response = await httpClient.get(API_ENDPOINTS.ASSESSMENT_SCHEDULE.SEARCH_COURSE, {
      params
    });
    return response.data;
  },

  searchModule: async (params: { search?: string; PageNumber?: number; PageSize?: number }) => {
    const response = await httpClient.get(API_ENDPOINTS.ASSESSMENT_SCHEDULE.LIST_MODULE, {
      params: {
        PageNumber: params?.PageNumber || 1,
        PageSize: params?.PageSize || 10,
        search: params?.search || ''
      }
    });
    return response.data;
  },

  getSummaryReport: async (msAssessmentScheduleId: string) => {
    try {
      const response = await httpClient.get(API_ENDPOINTS.ASSESSMENT_SCHEDULE.SUMMARY_REPORT_GET, {
        params: { msAssessmentScheduleId }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  upsertSummaryReport: async (data: {
    trAssessmentReportSummary?: string;
    msAssessmentScheduleId: string;
    summaryDescription: string;
  }) => {
    try {
      // If trAssessmentReportSummary exists, use PUT for update; otherwise use POST for add
      if (data.trAssessmentReportSummary) {
        // Update endpoint (PUT)
        const response = await httpClient.put(
          API_ENDPOINTS.ASSESSMENT_SCHEDULE.SUMMARY_REPORT_UPDATE,
          data
        );
        return response.data;
      } else {
        // Add endpoint (POST)
        const { trAssessmentReportSummary, ...addPayload } = data;
        const response = await httpClient.post(
          API_ENDPOINTS.ASSESSMENT_SCHEDULE.SUMMARY_REPORT_ADD,
          addPayload
        );
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }
  /* eslint-enable */
};
